export interface GuardrailAttachment {
  id: string;
  stage: 'input' | 'output' | 'both';
}

export interface CustomSkill {
  id: string;
  name: string;
  description: string;
  workflow_id: string;
  input_mapping?: Record<string, string> | null;
  tags: string[];
}

export interface SkillOverride {
  display_name?: string | null;
  description?: string | null;
  tags?: string[] | null;
  input_mapping?: Record<string, string> | null;
}

export interface PeerAgentAuthConfig {
  secret_ref?: string | null;
  header_name?: string | null;
}

export interface Agent {
  orgid: string;
  id: string;
  name: string;
  description: string;
  icon: string;
  color: string;

  model_provider: string;
  model_name: string;
  system_prompt: string;
  max_tokens: number;
  temperature: number;
  top_p: number;
  thinking_enabled: boolean;
  thinking_budget_tokens: number;

  enable_workflow_runtime: boolean;
  enable_workflow_authoring: boolean;
  enable_a2a_outbound: boolean;
  enable_scheduling?: boolean;

  // Absence means ON; off = orchestrator won't stream file links into chat.
  render_files_in_chat?: boolean;

  // Absence means OFF: interim files stay hidden until opted in per conversation.
  show_intermediate_files?: boolean;

  // Absence means OFF; ON also unions the user's own/shared workflows into the allowlist.
  allow_user_workflows?: boolean;

  // Absence means OFF; ON mounts the GitHub repo tools (needs the org GitHub credential).
  enable_github_tools?: boolean;

  // Absence means OFF: no cross-session per-user memory.
  memory_enabled?: boolean;

  // 0 or absent keeps memories forever; a positive value expires older entries.
  memory_retention_days?: number;

  // Default OFF; scores the amended task against the node's persona/goal and fails below the threshold.
  enable_task_alignment_guardrail?: boolean;
  task_alignment_min_relevance?: number;

  // Backend clamps every E2B sandbox value server-side.
  e2b_enabled?: boolean;
  e2b_template?: string;
  e2b_run_timeout_s?: number;
  e2b_max_edit_calls_per_turn?: number;
  e2b_allow_internet_access?: boolean;
  e2b_sandbox_env?: Record<string, string>;

  // Synthetic-data policy. Absence / false (default) refuses generation
  // and injects a platform-owned policy into the system prompt. True
  // restores the consent-gated sample-file offer.
  allow_synthetic_data_generation?: boolean;

  // Sparse per-agent tool-group overrides (group_id -> bool). Absent
  // keys resolve through the group's default chain server-side; see
  // AgentToolSettingsResponse for the resolved view.
  tool_settings?: Record<string, boolean>;

  workflow_allowlist: string[];
  workflow_allowlist_labels: string[];
  mcp_tool_allowlist: string[];
  data_collection_allowlist: string[];
  guardrail_allowlist: GuardrailAttachment[];
  peer_agent_allowlist: string[];
  custom_skills: CustomSkill[];
  skill_overrides: Record<string, SkillOverride>;

  runtime_canvas_workflow_id?: string | null;
  is_capitol_default: boolean;
  is_org_primary?: boolean;
  appears_in_node_explorer: boolean;

  exposed_via_a2a: boolean;
  a2a_bearer_token_prefix: string;
  a2a_bearer_issued_at: string;

  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface AgentCreateRequest {
  name: string;
  description?: string;
  icon?: string;
  color?: string;

  model_provider?: string;
  model_name?: string;
  system_prompt?: string;
  max_tokens?: number;
  temperature?: number;
  top_p?: number;
  thinking_enabled?: boolean;
  thinking_budget_tokens?: number;

  enable_workflow_runtime?: boolean;
  enable_workflow_authoring?: boolean;
  enable_a2a_outbound?: boolean;
  enable_scheduling?: boolean;
  render_files_in_chat?: boolean;
  show_intermediate_files?: boolean;
  allow_user_workflows?: boolean;
  enable_github_tools?: boolean;
  memory_enabled?: boolean;
  memory_retention_days?: number;

  enable_task_alignment_guardrail?: boolean;
  task_alignment_min_relevance?: number;

  e2b_enabled?: boolean;
  e2b_template?: string;
  e2b_run_timeout_s?: number;
  e2b_max_edit_calls_per_turn?: number;
  e2b_allow_internet_access?: boolean;
  e2b_sandbox_env?: Record<string, string>;
  allow_synthetic_data_generation?: boolean;

  // Sparse tool-group overrides; the full map replaces the stored one.
  tool_settings?: Record<string, boolean> | null;

  workflow_allowlist?: string[];
  workflow_allowlist_labels?: string[];
  mcp_tool_allowlist?: string[];
  data_collection_allowlist?: string[];
  guardrail_allowlist?: GuardrailAttachment[];
  peer_agent_allowlist?: string[];
  custom_skills?: CustomSkill[];
  skill_overrides?: Record<string, SkillOverride>;

  exposed_via_a2a?: boolean;
  suppress_bearer_delivery?: boolean;
}

export type AgentUpdateRequest = Partial<AgentCreateRequest> & {
  is_org_primary?: boolean;
};

// ---------------------------------------------------------------------------
// Tool groups (per-agent clickable tool settings)
//
// The registry -- group ids, defaults, and the toggleable-vs-locked split --
// lives in agentic-backend's tool-groups module and is proxied by
// platform-api, so this UI always renders from the same data the chat
// runtime resolves against.
// ---------------------------------------------------------------------------

export type AgentToolGroupSource =
  | 'override'
  | 'contextual_default'
  | 'legacy_flag'
  | 'default'
  | 'locked';

export interface AgentToolGroup {
  id: string;
  label: string;
  description: string;
  kind: 'toggleable' | 'locked';
  legacy_flag?: string | null;
  locked_reason?: string | null;
  example_tools?: string[];
  // Registry-only shape (GET /agents/{org}/tool-groups):
  default?: boolean | null;
  // Resolved shape (GET /agents/{org}/{agent}/tool-settings):
  enabled?: boolean;
  source?: AgentToolGroupSource;
  // What the group resolves to WITHOUT a tool_settings override -- the
  // "default for this agent" the UI offers to reset to.
  default_effective?: boolean;
  override?: boolean | null;
  // Locked rows: one-line provisioning status (e.g. mount counts).
  detail?: string;
  // file_output only: honest sub-gates (create_artifact additionally
  // requires allow_synthetic_data_generation).
  sub_gates?: Record<string, boolean>;
  // Org-server toggle rows only: the org_mcp_tool row uuid behind the
  // dynamic org_server_<id> group.
  org_server_id?: string;
}

export interface AgentToolGroupsResponse {
  version: number;
  groups: AgentToolGroup[];
  // Dynamic per-server org tool toggles (one per org-registered MCP
  // server: Figma, Jira, warehouse rows, ...). Present on the resolved
  // per-agent shape; the ids are ordinary tool_settings keys, so the
  // existing save path persists them unchanged.
  org_server_groups?: AgentToolGroup[];
}

export type AgentSortField =
  | 'name'
  | 'exposed_via_a2a'
  | 'model_name'
  | 'workflow_scope'
  | 'created_at'
  | 'updated_at';

export interface AgentCreateResponse {
  success: boolean;
  message: string;
  agent: Agent;
  a2a_bearer_token: string;
  a2a_endpoint_url: string;
  a2a_agent_card_url: string;
}

export interface AgentListResponse {
  success: boolean;
  message: string;
  agents: Agent[];
  count: number;
}

export interface AgentResponse {
  success: boolean;
  message: string;
  agent: Agent | null;
  // Computed endpoints for the A2A connection panel.  Returned on every
  // read so the FE never has to know where the agentic-backend lives --
  // platform-api owns AGENTIC_BACKEND_BASE_URL and renders both URLs
  // from it.  Empty when ``agent`` is null.  Mirrors platform-api's
  // ``schemas/agents.py::AgentResponse``.
  a2a_endpoint_url?: string;
  a2a_agent_card_url?: string;
}

export interface A2ATokenRotateResponse {
  success: boolean;
  message: string;
  a2a_bearer_token: string;
  a2a_bearer_token_prefix: string;
  a2a_bearer_issued_at: string;
}

/**
 * One workflow's compact summary for the description generator.
 * Populated FE-side from the workflow's full payload when the operator
 * ticks "Describe from workflows" on the agent config page -- gives the
 * LLM the actual graph + input shape so it can describe what the agent
 * can DO with each workflow rather than just listing names.
 */
export interface WorkflowSummaryForDescription {
  id?: string;
  name?: string;
  description?: string;
  /** Distinct ``type`` values from the workflow's nodes[]. */
  node_kinds?: string[];
  /** Distinct input-port categories the workflow accepts (file / string / enum / ...). */
  input_kinds?: string[];
}

export interface AutoGenerateAgentDescriptionRequest {
  name?: string;
  system_prompt?: string;
  model_name?: string;
  workflow_allowlist?: string[];
  workflow_allowlist_labels?: string[];
  mcp_tool_allowlist?: string[];
  data_collection_allowlist?: string[];
  peer_agent_allowlist?: string[];
  enable_workflow_runtime?: boolean;
  enable_workflow_authoring?: boolean;
  enable_a2a_outbound?: boolean;
  enable_scheduling?: boolean;
  workflow_names?: string[];
  /**
   * Richer per-workflow context populated by the agent config page when
   * "Describe from workflows" is checked.  Empty/omitted preserves the
   * legacy behaviour (just names + ids in the prompt).
   */
  workflow_summaries?: WorkflowSummaryForDescription[];
}

export interface AutoGenerateAgentDescriptionResponse {
  success: boolean;
  description: string;
}

export interface PeerAgent {
  orgid: string;
  id: string;
  name: string;
  description: string;
  base_url: string;
  auth_type: 'none' | 'bearer' | 'api_key';
  auth_config?: PeerAgentAuthConfig | null;
  agent_card_cache?: Record<string, unknown> | null;
  card_etag: string;
  created_at: string;
  updated_at: string;
}

export interface PeerAgentCreateRequest {
  name: string;
  description?: string;
  base_url: string;
  auth_type: 'none' | 'bearer' | 'api_key';
  auth_config?: PeerAgentAuthConfig | null;
}

export type PeerAgentUpdateRequest = Partial<PeerAgentCreateRequest>;

export interface PeerAgentListResponse {
  success: boolean;
  message: string;
  peer_agents: PeerAgent[];
  count: number;
}

export interface PeerAgentResponse {
  success: boolean;
  message: string;
  peer_agent: PeerAgent | null;
}

export interface AgentModelOption {
  value: string;
  label: string;
  provider: 'anthropic' | 'openai' | 'google' | 'openrouter';
  maxOutputTokens: number;
  supportsThinking: boolean;
}

export const AGENT_MODELS: AgentModelOption[] = [
  {
    value: 'claude-opus-5-5-fast',
    label: 'Claude Opus 5.5 Fast',
    provider: 'anthropic',
    maxOutputTokens: 128_000,
    supportsThinking: true,
  },
  {
    value: 'claude-opus-5-5',
    label: 'Claude Opus 5.5',
    provider: 'anthropic',
    maxOutputTokens: 128_000,
    supportsThinking: true,
  },
  {
    value: 'claude-opus-5-fast',
    label: 'Claude Opus 5 Fast',
    provider: 'anthropic',
    maxOutputTokens: 128_000,
    supportsThinking: true,
  },
  {
    value: 'claude-opus-5',
    label: 'Claude Opus 5',
    provider: 'anthropic',
    maxOutputTokens: 128_000,
    supportsThinking: true,
  },
  {
    value: 'claude-opus-4-8-fast',
    label: 'Claude Opus 4.8 Fast',
    provider: 'anthropic',
    maxOutputTokens: 128_000,
    supportsThinking: true,
  },
  {
    value: 'claude-opus-4-8',
    label: 'Claude Opus 4.8',
    provider: 'anthropic',
    maxOutputTokens: 128_000,
    supportsThinking: true,
  },
  {
    value: 'claude-sonnet-5-5',
    label: 'Claude Sonnet 5.5',
    provider: 'anthropic',
    maxOutputTokens: 128_000,
    supportsThinking: true,
  },
  {
    value: 'claude-sonnet-4-6',
    label: 'Claude Sonnet 4.6',
    provider: 'anthropic',
    maxOutputTokens: 64_000,
    supportsThinking: true,
  },
  {
    value: 'gpt-5.6',
    label: 'GPT-5.6',
    provider: 'openai',
    maxOutputTokens: 64_000,
    supportsThinking: true,
  },
  {
    value: 'gpt-5.5',
    label: 'GPT-5.5',
    provider: 'openai',
    maxOutputTokens: 64_000,
    supportsThinking: true,
  },
  {
    value: 'gpt-5.5-mini',
    label: 'GPT-5.5 mini',
    provider: 'openai',
    maxOutputTokens: 32_000,
    supportsThinking: true,
  },
  {
    value: 'gemini-2.5-pro',
    label: 'Gemini 2.5 Pro',
    provider: 'google',
    maxOutputTokens: 32_000,
    supportsThinking: false,
  },
  {
    value: 'moonshotai/kimi-k3',
    label: 'Kimi K3',
    provider: 'openrouter',
    maxOutputTokens: 131_072,
    supportsThinking: true,
  },
  {
    value: 'z-ai/glm-5.2',
    label: 'GLM 5.2',
    provider: 'openrouter',
    maxOutputTokens: 131_072,
    supportsThinking: true,
  },
  {
    value: 'deepseek/deepseek-v4-pro',
    label: 'DeepSeek V4 Pro',
    provider: 'openrouter',
    maxOutputTokens: 131_072,
    supportsThinking: true,
  },
];
